import { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { viewForPath, type PublicView } from '../../data/public-routes.js'

export type ViewState = PublicView | 'future-chat'

const getViewFromPath = (pathname: string): ViewState => viewForPath(pathname)

export function useAppRouting() {
  const location = useLocation()
  const navigate = useNavigate()

  const [view, setView] = useState<ViewState>(() => getViewFromPath(location.pathname))

  // Legacy route: /future-chat → /chat
  useEffect(() => {
    if (location.pathname === '/future-chat') {
      void navigate('/chat', { replace: true })
    }
  }, [location.pathname, navigate])

  // Sync view state with URL changes
  useEffect(() => {
    const newView = getViewFromPath(location.pathname)
    if (newView !== view) {
      setView(newView)
    }
  }, [location.pathname, view])

  // Update URL when view changes (but not from URL change)
  const setViewAndNavigate = useCallback(
    (newView: ViewState) => {
      setView(newView)
      const pathMap: Record<ViewState, string> = {
        admin: '/admin',
        about: '/about',
        chat: '/chat',
        privacy: '/privacy',
        terms: '/terms',
        landing: '/landing',
        'ai-agency': '/ai-agency',
        fixer: '/fixer',
        workshops: '/workshops',
        'not-found': '/404',
        'future-chat': '/future-chat',
      }
      void navigate(pathMap[newView] ?? '/landing', { replace: true })
    },
    [navigate]
  )

  return {
    view,
    setView: setViewAndNavigate,
  }
}
